import axios from 'axios'
import MockAdapter from 'axios-mock-adapter'
import { sanitizeInput } from 'Utility/globalMethods'
import { currentTime, writeToLog } from 'Utility/logger'
import { Capacitor, CapacitorHttp } from '@capacitor/core'
import requestManager from './requestManager'

const handleMalformedAnonToken = (responseData) => {
  try {
    const cleanedJsonString = responseData.replace(/,\s*}$/, '}').trim()
    const parsedData = JSON.parse(cleanedJsonString)
    if (parsedData.APIGW_anonJWE) {
      window.sessionStorage.setItem('anon_jwe', parsedData.APIGW_anonJWE)
      return parsedData
    }
  } catch (parseError) {
    console.error('Error parsing malformed anon token response:', parseError)
  }
  return responseData
}

const interceptResponseAndSetJwe = (response) => {
  if (response.data && response.data.access_token) {
    window.sessionStorage.setItem('jwe', response.data.access_token)
  } else if (response.data && response.data.APIGW_anonJWE) {
    // this is an anon token for not logged routes
    window.sessionStorage.setItem('anon_jwe', response.data.APIGW_anonJWE)
  } else if (typeof response.data === 'string' && response.data.includes('APIGW_anonJWE')) {
    response.data = handleMalformedAnonToken(response.data)
  } else if (response.data && response.data.new_access_token) {
    window.sessionStorage.removeItem('jwe')
    window.sessionStorage.setItem('jwe', response.data.new_access_token)
  } else if (response.data && response.data.token_info && response.data.token_info.access_token) {
    window.sessionStorage.setItem('jwe', response.data.token_info.access_token)
  }
}

const integrationEnvironmentHeaders = {
  development: 'INT1',
  development2: 'INT2',
  development3: 'INT3'
}

const defaultHeaders = {
  Accept: 'application/json, text/plain, */*',
  Application: process.env.BANK === 'NEXIBUSINESS' ? 'NB' : process.env.BANK,
  'cache-control': 'no-cache',
  Channel: Capacitor.isNativePlatform() ? 'mobile' : 'web',
  'Content-Type': 'application/json',
  pragma: 'no-cache',
  ...(process.env.BANK === 'NEXIBUSINESS' && integrationEnvironmentHeaders[process.env.NODE_ENV]
    ? { ENV: integrationEnvironmentHeaders[process.env.NODE_ENV] }
    : {}),
  ...(Capacitor.isNativePlatform() ? { Origin: process.env.APPLICATION_URL, Referrer: process.env.APPLICATION_URL, Version: process.env.VERSION_NUMBER } : {}) // 👈 qui
  // ...(Capacitor.isNativePlatform() && window.sessionStorage.getItem('X-Client-Session') ? { 'X-Client-Session': window.sessionStorage.getItem('X-Client-Session') } : {})
  // ...(process.env.BANK === 'NEXIBUSINESS' ? { 'x-dev-slider': 'SKIP' } : {})
}

// Axios configuration
const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: Capacitor.isNativePlatform() ? process.env.API_REST_BE_MOBILE : process.env.API_REST_BE,
  headers: {
    common: { ...defaultHeaders }
  }
})

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (request) => {
    request.headers.Locale = (window.localStorage.getItem('userLanguage') || (window.navigator.language || window.navigator.userLanguage)?.substring(0, 2))?.toUpperCase()

    if (request.url !== '/oauth2/token') {
      if (window.sessionStorage.getItem('jwe')) {
        request.headers.Authorization = `Bearer ${window.sessionStorage.getItem('jwe')}`
      } else if (window.sessionStorage.getItem('anon_jwe')) {
        request.headers.Authorization = `Bearer ${window.sessionStorage.getItem('anon_jwe')}`
      }
      if (Capacitor.isNativePlatform() && window.sessionStorage.getItem('x-client-session')) {
        const sessionHeader = 'X-Client-Session'
        request.headers[sessionHeader] = window.sessionStorage.getItem('x-client-session')
      }
    }

    const avoidSanitizeUrls = [
      '/services/merchant-service/public/aml/personal-docs'
    ]
    const isFormData = typeof FormData !== 'undefined' && request.data instanceof FormData

    if (isFormData) {
      if (request.headers?.delete) {
        request.headers.delete('Content-Type')
      } else {
        delete request.headers['Content-Type']
        delete request.headers['content-type']
      }
    }

    if (!isFormData && !avoidSanitizeUrls.some(url => request.url.includes(url))) {
      request.data = sanitizeInput(request.data)
    }

    if (Capacitor.isNativePlatform()) {
      let urlWithParams = `${request.baseURL}${request.url.charAt(0) === '/' ? '' : '/'}${request.url}`

      if (request.params && Object.keys(request.params).length !== 0) {
        const keys = Object.keys(request.params)
        const newUrl = urlWithParams.includes('?') ? `${urlWithParams}&` : `${urlWithParams}?`
        const params = new URLSearchParams()
        for (let i = 0; i < keys.length; i++) {
          params.append(keys[i], request.params[keys[i]])
        }
        urlWithParams = `${newUrl}${params.toString()}`
      }
      return new Promise((resolve, reject) => {
        const requestObj = {
          method: request.method.toUpperCase(),
          url: urlWithParams,
          headers: request.headers,
          responseType: request.responseType,
          credentials: request.withCredentials
        }
        if (request.method.toUpperCase() === 'PUT' || request.method.toUpperCase() === 'POST') {
          requestObj.data = request.data
          if (isFormData) requestObj.dataType = 'formData'
        }
        CapacitorHttp.request(requestObj)
          .then(response => {
            const newResponse = {
              data: response.data,
              status: response.status,
              statusText: response.statusText ?? '',
              headers: response.headers,
              config: request,
              request
            }
            interceptResponseAndSetJwe(newResponse)
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({
              isAxiosError: false,
              response: newResponse
            })
          })
          .catch(err => {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({
              isAxiosError: true,
              response: {
                data: err?.error ?? null,
                status: err?.status ?? 500,
                statusText: err?.message ?? 'CapacitorHttp Error',
                headers: err?.headers ?? {},
                config: request
              }
            })
          })
      })
    }

    // Registra la richiesta nel requestManager SOLO se esplicitamente richiesto con cancelOnRouteChange: true
    // Questo approccio opt-in evita regressioni: di default le richieste NON vengono cancellate
    if (!request.signal && request.cancelOnRouteChange === true) {
      const { signal, requestId } = requestManager.register(request.url)
      request.signal = signal
      request._requestManagerId = requestId
    }

    return request
  },
  (error) => error
)

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Rimuovi la richiesta dal registro delle richieste pending
    if (response.config && response.config._requestManagerId) {
      requestManager.unregister(response.config._requestManagerId)
    }

    interceptResponseAndSetJwe(response)
    if (process.env.DEBUG_MODE === 'true') {
      if (!/:4001/.test(response.config.url)) {
        writeToLog({
          type: 'debug',
          msg: `{"time": "${currentTime()}", "msg": "Invoke method ${response.config.method}", "rest": "Api ${response.config.url
            }", "request": ${response.config.data === undefined ? JSON.stringify('empty') : `${response.config.data}`
            }, "response": ${`${response.data === undefined ? JSON.stringify('empty') : JSON.stringify(response.data)
            }`}}`
        })
      }
    }
    // This logic has been created because the BE cannot filter data.data
    /* if (response.data && response.data.data) {
      return {
        ...response,
        data: response.data.data
      }
    } */
    return response
  },
  (error) => {
    // Rimuovi la richiesta dal registro delle richieste pending
    if (error?.config?._requestManagerId) {
      requestManager.unregister(error.config._requestManagerId)
    }

    // Se la richiesta è stata cancellata, restituisci un oggetto specifico per la gestione
    if (requestManager.isAbortError(error)) {
      return {
        _cancelled: true,
        status: 0,
        data: null
      }
    }

    if (process.env.DEBUG_MODE === 'true') {
      if (error?.config?.url && !/:4001/.test(error.config.url)) {
        writeToLog({
          type: 'error',
          msg: `{"time": "${currentTime()}", "msg": "Invoke method ${error.config.method}", "rest": "Api ${error.config.url
            }", "request": ${error.config.data === undefined ? JSON.stringify('empty') : `${error.config.data}`
            }, "response": ${`${error.response?.data === undefined ? JSON.stringify('empty') : JSON.stringify(error.response.data)
            }`}}`
        })
      }
    }
    return error.response
  }
)
const mockInstance = new MockAdapter(axiosInstance, { delayResponse: 1500 })

if (process.env.ISMOCK === 'false') {
  mockInstance.onAny().passThrough()
} else {
  mockInstance.onGet(/^http:\/\/localhost:4001\/*/).passThrough()
  mockInstance.onPost(/^http:\/\/localhost:4001\/*/).passThrough()
  mockInstance.onPut(/^http:\/\/localhost:4001\/*/).passThrough()
}
export { defaultHeaders, mockInstance, axiosInstance, requestManager }
